import { useCallback, useEffect, useState } from "react";
import { cleanPrefs, FLAVOUR_FAMILIES, FLAVOUR_GUEST_LABELS, MAX_PREFERRED_FAMILIES, SPEC_STYLES } from "@baros/core";
import { ApiError } from "../api.js";
import { guestPageApi, type GuestPageData } from "./api.js";
import "./guests.css";

const POLL_MS = 20_000;

function money(n: number): string {
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

/**
 * The guest page (#126): what a guest in the queue sees when they open the
 * link in their text, on their own phone, signed in to nothing.
 *
 * Where they are in the queue comes first — it is why they opened it. Then
 * tonight's drinks, the offer that makes the link worth clicking. Then,
 * optional and after the menu rather than before it, an email, a separate
 * unticked box for offers, and what they like, which is what makes them
 * recognisable next time.
 */
export function GuestPage({ token }: { readonly token: string }) {
  const [data, setData] = useState<GuestPageData | null>(null);
  const [gone, setGone] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await guestPageApi.get(token));
    } catch (err) {
      setGone(err instanceof ApiError && err.status === 404 ? err.message : "This page couldn't load. Check your signal and try again.");
    }
  }, [token]);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (data?.venue.name) document.title = data.venue.name;
  }, [data?.venue.name]);

  if (gone && !data) {
    return (
      <div className="gp">
        <div className="gp-card">
          <div className="gp-status">{gone}</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="gp"><div className="label">Loading…</div></div>;

  const { party } = data;
  return (
    <div className="gp">
      <header className="gp-head">
        <div className="gp-venue">{data.venue.name}</div>
      </header>

      <div className={`gp-card gp-place ${party.status}`}>
        {party.status === "waiting" && (
          <>
            <div className="gp-status">You're on the list, {party.name}.</div>
            <div className="gp-sub">
              {party.ahead === 0 ? "You're next." : `${party.ahead} part${party.ahead === 1 ? "y" : "ies"} ahead of you.`} We'll text
              you when your table's ready — you'll have 15 minutes to get to the door.
            </div>
          </>
        )}
        {party.status === "ready" && (
          <>
            <div className="gp-status">Your table's ready.</div>
            <div className="gp-sub">Please come to the door within 15 minutes.</div>
          </>
        )}
        {party.status === "seated" && <div className="gp-status">Enjoy your night, {party.name}.</div>}
        {(party.status === "cancelled" || party.status === "no-show") && (
          <div className="gp-status">You're no longer in the queue. Ask at the door if that's a mistake.</div>
        )}
      </div>

      {data.suggestions.length > 0 && (
        <div className="gp-card">
          <div className="gp-h">You might like</div>
          <div className="gp-sub">{data.suggestions.join(" · ")}</div>
        </div>
      )}

      {data.menu.length > 0 && (
        <div className="gp-card">
          <div className="gp-h">Tonight's drinks</div>
          <ul className="gp-menu">
            {data.menu.map((m) => (
              <li key={m.name}>
                <span className="n">{m.name}</span>
                {m.style.length > 0 && <span className="s">{m.style.join(" · ")}</span>}
                {data.showPrices && m.price !== null && <span className="p num">{money(m.price)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.saved && <StayInTouch token={token} venue={data.venue.name} saved={data.saved} onSaved={() => void load()} />}

      {(party.status === "waiting" || party.status === "ready") && (
        <button
          className="gp-leave"
          onClick={async () => {
            if (!window.confirm("Give up your place in the queue?")) return;
            await guestPageApi.cancel(token).catch(() => undefined);
            await load();
          }}
        >
          Can't wait any longer? Give up your place
        </button>
      )}

      <footer className="gp-foot">
        Your number is only used for this queue. {data.venue.name} keeps what you give here for up to two years after
        your last visit — ask staff any time to see it or delete it.
      </footer>
    </div>
  );
}

function StayInTouch(props: {
  readonly token: string;
  readonly venue: string;
  readonly saved: NonNullable<GuestPageData["saved"]>;
  readonly onSaved: () => void;
}) {
  const [email, setEmail] = useState("");
  // Starts from what they chose before, so saving their tastes alone never
  // quietly withdraws a "yes" — or turns a "no" into one.
  const [marketing, setMarketing] = useState(props.saved.marketing);
  const [aromas, setAromas] = useState<string[]>([...cleanPrefs(props.saved.prefs).aromas]);
  const [styles, setStyles] = useState<string[]>([...props.saved.prefs.styles]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return (
      <div className="gp-card">
        <div className="gp-h">Thanks</div>
        <div className="gp-sub">Saved. We'll remember what you like next time.</div>
      </div>
    );
  }

  return (
    <form
      className="gp-card gp-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await guestPageApi.save(props.token, {
            ...(email.trim() ? { email: email.trim() } : {}),
            ...(email.trim() || props.saved.hasEmail ? { marketing } : {}),
            aromas,
            styles,
          });
          setDone(true);
          props.onSaved();
        } catch (err) {
          setError(err instanceof Error ? err.message : "Couldn't save that");
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="gp-h">While you wait</div>
      <div className="gp-sub">All optional.</div>

      <div className="gp-q">What do you like in a drink? Pick up to {MAX_PREFERRED_FAMILIES}.</div>
      <div className="g-aromas">
        {FLAVOUR_FAMILIES.map(({ key: family, colour }) => {
          const on = aromas.includes(family);
          return (
            <button
              key={family}
              type="button"
              className="g-aroma"
              aria-pressed={on}
              style={{ ["--c" as string]: colour }}
              disabled={!on && aromas.length >= MAX_PREFERRED_FAMILIES}
              onClick={() => setAromas(on ? aromas.filter((a) => a !== family) : [...aromas, family])}
            >
              {FLAVOUR_GUEST_LABELS[family]}
            </button>
          );
        })}
      </div>
      <div className="g-styles">
        {SPEC_STYLES.map((s) => (
          <button
            key={s}
            type="button"
            className="btn sm"
            aria-pressed={styles.includes(s)}
            onClick={() => setStyles(styles.includes(s) ? styles.filter((x) => x !== s) : [...styles, s])}
          >
            {s}
          </button>
        ))}
      </div>

      <label className="field">
        <span>Email{props.saved.hasEmail ? " (we have one — enter a new one to change it)" : ""}</span>
        <input type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      {(email.trim() || props.saved.hasEmail) && (
        <label className="gp-consent">
          <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
          <span>Send me news and offers from {props.venue}. Unsubscribe any time.</span>
        </label>
      )}

      {error && <div className="msg err">{error}</div>}
      <button className="btn primary" type="submit" disabled={busy || (aromas.length === 0 && styles.length === 0 && !email.trim())}>
        {busy ? "Saving…" : "Save"}
      </button>
    </form>
  );
}
